<template>
  <div class="daily-report-page">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运营部</el-breadcrumb-item>
      <el-breadcrumb-item>日报管理</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主内容区 -->
    <el-card class="main-card" shadow="hover">
      <div slot="header" class="card-header">
        <span>日报管理</span>
        <div class="header-actions">
          <el-button type="primary" size="small" icon="el-icon-document-add" @click="openGenerateDialog">生成日报</el-button>
          <el-button type="success" size="small" icon="el-icon-promotion"
            :disabled="selectedRows.length === 0" :loading="pushing"
            @click="handleBatchPush">
            批量推送<span v-if="selectedRows.length">（{{ selectedRows.length }}）</span>
          </el-button>
        </div>
      </div>
      <!-- 筛选工具栏 -->
      <div class="toolbar">
        <el-input
          v-model="queryInfo.keyword"
          placeholder="项目名称/店铺名称/群名称"
          clearable
          size="small"
          style="width: 200px"
          @clear="handleSearch"
        ></el-input>
        <el-date-picker
          v-model="queryInfo.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          size="small"
          style="width: 260px"
          value-format="yyyy-MM-dd"
          @change="handleSearch"
        ></el-date-picker>
        <el-select
          v-model="queryInfo.status"
          placeholder="发送状态"
          clearable
          size="small"
          style="width: 120px"
          @change="handleSearch"
        >
          <el-option label="未发送" value="unsent"></el-option>
          <el-option label="已发送" value="sent"></el-option>
        </el-select>
        <el-select
          v-model="queryInfo.currentOperatorId"
          placeholder="现阶段负责人"
          clearable
          filterable
          size="small"
          style="width: 150px"
          @change="handleSearch"
        >
          <el-option v-for="item in operatorOptions" :key="item._id" :label="item.name" :value="item._id"></el-option>
        </el-select>
        <el-select
          v-model="queryInfo.brand"
          placeholder="品牌"
          clearable
          size="small"
          style="width: 120px"
          @change="handleSearch"
        >
          <el-option v-for="item in brandList" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 列表表格 -->
      <el-table
        :data="list"
        border
        size="small"
        v-loading="loading"
        :height="tableHeight"
        class="data-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="45" :selectable="canSelect"></el-table-column>
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        <el-table-column prop="date" label="日期" width="110"></el-table-column>
        <el-table-column label="店铺名称" min-width="180">
          <template slot-scope="scope">
            <div style="font-size:14px;color:#333;font-weight:600;">{{ scope.row.projectName }}</div>
            <div style="color:#909399;font-size:12px;">{{ scope.row.storeName }}</div>
          </template>
        </el-table-column>
        <el-table-column label="品牌" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.brand" size="mini">{{ scope.row.brand }}</el-tag>
            <span v-else style="color: #c0c4cc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="企微群" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.groupName">{{ scope.row.groupName }}</span>
            <el-tag v-else type="info" size="mini">未绑定</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="200">
          <template slot-scope="scope">
            <el-tag
              v-for="p in (scope.row.platforms || [])"
              :key="p.platform"
              :type="p.hasData ? 'success' : 'info'"
              size="mini"
              style="margin-right: 4px"
            >{{ p.platformLabel }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="发送状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'sent' ? 'success' : 'info'" size="mini">
              {{ scope.row.status === 'sent' ? '已发送' : '未发送' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="showDetail(scope.row)">查看详情</el-button>
            <el-button type="text" size="mini" @click="copyContent(scope.row)">复制日报</el-button>
            <el-button v-if="scope.row.status === 'unsent'" type="text" size="mini" @click="handlePushOne(scope.row)">推送</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagenum"
        :page-sizes="[20, 50, 100]"
        :page-size="pagesize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        class="pagination"
      ></el-pagination>
    </el-card>

    <!-- 生成日报弹窗 -->
    <el-dialog title="生成日报" :visible.sync="generateDialogVisible" width="420px" :close-on-click-modal="false">
      <el-form label-width="80px" size="small">
        <el-form-item label="选择日期">
          <el-date-picker
            v-model="generateDate"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            :picker-options="generatePickerOptions"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-alert
          title="将为所有「运营中」的店铺生成该日期的日报，已存在的日报会被覆盖更新"
          type="info"
          :closable="false"
          show-icon
        ></el-alert>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="generateDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="generating" @click="handleGenerate">确认生成</el-button>
      </span>
    </el-dialog>

    <!-- 日报详情弹窗 -->
    <el-dialog
      :title="detailData ? detailData.storeName + ' - ' + detailData.date + ' 日报' : '日报详情'"
      :visible.sync="detailDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      custom-class="detail-dialog"
    >
      <div v-if="detailData" v-loading="detailLoading" class="detail-content">
        <!-- 店铺标题 -->
        <div class="detail-header">
          <span class="detail-store-name">{{ detailData.storeName }}</span>
          <el-tag
            :type="detailData.status === 'sent' ? 'success' : 'info'"
            size="mini"
            style="margin-left: 8px"
          >{{ detailData.status === 'sent' ? '已发送' : '未发送' }}</el-tag>
        </div>

        <!-- 各平台数据区块 -->
        <div
          v-for="p in (detailData.platforms || [])"
          :key="p.platform"
          class="platform-section"
        >
          <div class="platform-title">{{ p.platformLabel }}</div>
          <div v-if="!p.hasData" class="platform-nodata">暂无数据</div>
          <div v-else class="platform-metrics">
            <div
              v-for="field in getFieldsForPlatform(p.platform)"
              :key="field.key"
              class="metric-item"
            >
              <span class="metric-label">{{ field.label }}</span>
              <span class="metric-value">{{ formatValue(p.metrics[field.key], field.format, field.precision) }}</span>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { brandList } from '@/utility/staticData'

// 日报字段配置（与后端 REPORT_FIELDS 对应，驱动详情渲染）
const REPORT_FIELDS = [
  { key: 'pingfen', label: '评分', format: 'number', precision: 1 },
  { key: 'dianpufen', label: '今日店铺分', format: 'number', platforms: ['meituan'] },
  { key: 'waimai_shouru', label: '实收', format: 'money' },
  { key: 'waimai_dingdan', label: '单量', format: 'number' },
  { key: 'waimai_kedan', label: '客单价', format: 'money' },
  { key: 'waimai_baoguang', label: '曝光', format: 'number' },
  { key: 'waimai_rudian_rate', label: '入店转化率', format: 'rate' },
  { key: 'waimai_xiadan_rate', label: '下单转化率', format: 'rate' },
  { key: 'paiming', label: '商圈排名', format: 'number' },
  { key: 'waimai_tuiguang', label: '推广消费', format: 'money' }
]

export default {
  data () {
    return {
      // 筛选条件
      brandList,
      queryInfo: {
        keyword: '',
        dateRange: null,
        status: '',
        currentOperatorId: '',
        brand: ''
      },

      // 员工列表（现阶段负责人下拉数据源）
      employeeList: [],

      // 列表数据
      list: [],
      total: 0,
      pagenum: 1,
      pagesize: 20,
      loading: false,

      // 批量推送
      selectedRows: [],
      pushing: false,

      // 生成日报弹窗
      generateDialogVisible: false,
      generateDate: '',
      generating: false,

      // 详情弹窗
      detailDialogVisible: false,
      detailData: null,
      detailLoading: false,

      // 生成弹窗日期快捷选项
      generatePickerOptions: {
        disabledDate (time) {
          // 不能选择今天及以后的日期
          return time.getTime() > Date.now() - 8.64e6 // 8.64e6 = 24h（允许选择今天）
        },
        shortcuts: [
          {
            text: '昨天',
            onClick (picker) {
              const date = new Date()
              date.setTime(date.getTime() - 3600 * 1000 * 24)
              picker.$emit('pick', date)
            }
          },
          {
            text: '前天',
            onClick (picker) {
              const date = new Date()
              date.setTime(date.getTime() - 3600 * 1000 * 24 * 2)
              picker.$emit('pick', date)
            }
          }
        ]
      }
    }
  },
  computed: {
    tableHeight () {
      return window.innerHeight - 300
    },
    // 现阶段负责人下拉选项（与 Dianpu.vue 保持一致的岗位过滤）
    operatorOptions () {
      return this.employeeList.filter(e => /运营师|运营主管|建店专员/.test(e.job))
    }
  },
  created () {
    // 默认生成日期为昨天
    this.generateDate = this.getYesterday()
    this.getEmployees()
    this.getList()
  },
  methods: {
    // 获取昨天的日期字符串
    getYesterday () {
      const d = new Date()
      d.setDate(d.getDate() - 1)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 获取运营部员工列表（现阶段负责人下拉数据源）
    async getEmployees () {
      try {
        const { data: res } = await this.$http.get('yunying/employees/list')
        if (res.meta.status !== 200) {
          console.error('获取员工列表失败:', res.meta.msg)
          return
        }
        this.employeeList = res.data || []
      } catch (e) {
        console.error('获取员工列表失败:', e)
      }
    },

    // 获取列表数据
    async getList () {
      this.loading = true
      try {
        const params = {
          pagenum: this.pagenum,
          pagesize: this.pagesize
        }
        if (this.queryInfo.keyword) {
          params.keyword = this.queryInfo.keyword
        }
        if (this.queryInfo.dateRange && this.queryInfo.dateRange.length === 2) {
          params.startDate = this.queryInfo.dateRange[0]
          params.endDate = this.queryInfo.dateRange[1]
        }
        if (this.queryInfo.status) {
          params.status = this.queryInfo.status
        }
        if (this.queryInfo.currentOperatorId) {
          params.currentOperatorId = this.queryInfo.currentOperatorId
        }
        if (this.queryInfo.brand) {
          params.brand = this.queryInfo.brand
        }

        const { data: res } = await this.$http.get('yunying/dailyReport/list', { params })
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        this.list = res.data.list || []
        this.total = res.data.total || 0
      } catch (error) {
        this.$message.error('获取日报列表失败')
        console.error('获取日报列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 查询
    handleSearch () {
      this.pagenum = 1
      this.getList()
    },

    // 重置
    handleReset () {
      this.queryInfo = { keyword: '', dateRange: null, status: '', currentOperatorId: '', brand: '' }
      this.pagenum = 1
      this.getList()
    },

    // 判断行是否可选（仅未发送的可勾选）
    canSelect (row) {
      return row.status === 'unsent'
    },

    // 选中行变更
    handleSelectionChange (val) {
      this.selectedRows = val
    },

    // 单条推送
    handlePushOne (row) {
      this.$confirm(
        `确认将「${row.storeName}」${row.date}的日报推送到企微群？`,
        '推送确认',
        { type: 'warning' }
      ).then(() => {
        this.doPush([row._id])
      }).catch(() => {})
    },

    // 批量推送
    handleBatchPush () {
      const n = this.selectedRows.length
      this.$confirm(
        `确认推送选中的 ${n} 份未发送日报？`,
        '批量推送确认',
        { type: 'warning' }
      ).then(() => {
        this.doPush(this.selectedRows.map(r => r._id))
      }).catch(() => {})
    },

    // 执行推送请求
    async doPush (ids) {
      this.pushing = true
      try {
        const { data: res } = await this.$http.post('yunying/dailyReport/push', { ids })
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        const s = res.data
        const parts = []
        if (s.queued) parts.push(`已入队${s.queued}条`)
        if (s.alreadySent) parts.push(`已发送跳过${s.alreadySent}条`)
        if (s.switchOff) parts.push(`未开开关${s.switchOff}条`)
        if (s.noGroup) parts.push(`未绑群${s.noGroup}条`)
        if (s.failed) parts.push(`入队失败${s.failed}条`)
        if (s.queued > 0) {
          this.$message.success(parts.length ? '已加入推送队列：' + parts.join('，') + '（将在数秒内发送）' : '已加入推送队列')
        } else {
          this.$message.warning(parts.length ? parts.join('，') : '未入队')
        }
        this.getList()
      } catch (error) {
        this.$message.error('推送失败')
        console.error('推送失败:', error)
      } finally {
        this.pushing = false
      }
    },

    // 分页大小变更
    handleSizeChange (newSize) {
      this.pagesize = newSize
      this.pagenum = 1
      this.getList()
    },

    // 页码变更
    handleCurrentChange (newPage) {
      this.pagenum = newPage
      this.getList()
    },

    // 打开生成弹窗
    openGenerateDialog () {
      this.generateDate = this.getYesterday()
      this.generateDialogVisible = true
    },

    // 确认生成
    async handleGenerate () {
      if (!this.generateDate) {
        this.$message.warning('请选择日期')
        return
      }
      this.generating = true
      try {
        const { data: res } = await this.$http.post('yunying/dailyReport/generate', {
          date: this.generateDate
        })
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        const s = res.data
        this.$message.success(`日报生成完成：共${s.total}家店铺，新增${s.generated}份，更新${s.updated}份${s.noPlatform > 0 ? `（${s.noPlatform}家无平台账号）` : ''}${s.noData > 0 ? `（${s.noData}家无运营数据）` : ''}`)
        this.generateDialogVisible = false
        // 刷新列表
        this.getList()
      } catch (error) {
        this.$message.error('生成日报失败')
        console.error('生成日报失败:', error)
      } finally {
        this.generating = false
      }
    },

    // 复制日报内容（与自动推送的 content 完全一致，可直接粘贴到企微群）
    async copyContent (row) {
      const text = row && row.content ? row.content : ''
      if (!text || !text.trim()) {
        this.$message.warning('该日报暂无内容')
        return
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          // 兼容旧环境：临时 textarea + execCommand
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$message.success('日报内容已复制，可粘贴到群中发送')
      } catch (error) {
        this.$message.error('复制失败，请重试')
        console.error('复制日报失败:', error)
      }
    },

    // 查看详情
    async showDetail (row) {
      this.detailDialogVisible = true
      this.detailData = null
      this.detailLoading = true
      try {
        const { data: res } = await this.$http.get(`yunying/dailyReport/detail/${row._id}`)
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          this.detailDialogVisible = false
          return
        }
        this.detailData = res.data
      } catch (error) {
        this.$message.error('获取日报详情失败')
        console.error('获取日报详情失败:', error)
        this.detailDialogVisible = false
      } finally {
        this.detailLoading = false
      }
    },

    // 获取平台应显示的字段列表
    getFieldsForPlatform (platform) {
      return REPORT_FIELDS.filter(f => !f.platforms || f.platforms.includes(platform))
    },

    // 格式化数值
    formatValue (value, format, precision) {
      if (value === null || value === undefined || value === '') return '-'
      const num = Number(value)
      switch (format) {
        case 'money':
          return num.toFixed(2)
        case 'rate':
          return (num * 100).toFixed(2) + '%'
        case 'number':
        default:
          if (precision !== undefined && precision !== null) {
            return num.toFixed(precision)
          }
          return String(value)
      }
    }
  }
}
</script>

<style scoped>
.daily-report-page {
  padding: 0 4px;
}

.breadcrumb {
  margin-bottom: 16px;
}

.main-card {
  border-radius: 8px;
  border: 1px solid #e8ecf1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0 12px;
  flex-wrap: wrap;
}

/* 分页 */
.pagination {
  padding: 14px 0 0;
  text-align: right;
}
</style>

<style>
/* 详情弹窗内容样式（非 scoped，因为 custom-class 渲染在 body） */
.detail-dialog .detail-content {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 12px;
}

.detail-dialog .detail-header {
  text-align: center;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.detail-dialog .detail-store-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.detail-dialog .platform-section {
  margin-bottom: 20px;
}

.detail-dialog .platform-title {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
  margin-bottom: 10px;
}

.detail-dialog .platform-nodata {
  color: #909399;
  font-size: 13px;
  text-align: center;
  padding: 8px 0;
}

.detail-dialog .platform-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 24px;
}

.detail-dialog .metric-item {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  line-height: 28px;
}

.detail-dialog .metric-label {
  color: #909399;
}

.detail-dialog .metric-value {
  color: #303133;
  font-weight: 500;
}
</style>
